import type { IconName } from "@/design/icons";
import { BILLING_SECTIONS } from "@/features/billing/sections";
import { tabAddress, visibleTabs } from "@/pages/settings-tabs";
import { type AccessModule, moduleAllowed } from "@/shared/access/modules";

/**
 * Os destinos do painel, UMA vez.
 *
 * Eram duas listas à mão — a do menu e a do ⌘K — e elas divergiram exatamente
 * como duas listas divergem: cinco telas (`/`, `/missions`, `/help`,
 * `/system`, `/account`) existiam no menu e não na paleta, e `/usage` estava
 * atrás do papel de plataforma na paleta sendo rota livre no router e item sem
 * papel no menu — a conta escondida de quem paga, do lado em que ninguém olhou.
 *
 * Aqui não há como divergir: quem desenha um destino lê deste arquivo, e o
 * `nav-items.spec.ts` prova contra `router.getRoutes()` que nenhuma tela do
 * painel ficou de fora.
 *
 * ── O QUE ESTE ARQUIVO NÃO SABE ───────────────────────────────────────────
 *
 * As entidades do tenant e os funis. Eles não são código: chegam de
 * `useStructure()` e de `usePipelines()` em tempo de execução, e é por isso que
 * criar uma tela no Construtor não pede deploy do front (F-ADR-009). Esta lista
 * é só a parte do produto que existe antes de o tenant dizer qualquer coisa.
 */
export interface NavItem {
	/** Um caminho, e não um `name`: é o que o menu e a paleta têm em comum. */
	readonly to: string;
	/**
	 * A CHAVE do rótulo, e não o rótulo.
	 *
	 * Uma tabela de destinos não pode carregar frase: em que idioma o painel está
	 * só se sabe na hora de desenhar, e o menu e a paleta desenham em momentos
	 * diferentes. As chaves apontam para `i18n/nav.ts`, ao lado deste arquivo.
	 */
	readonly labelKey: string;
	readonly icon: IconName;
	readonly group: NavGroupKey;
	/**
	 * O que a tela responde, em três a cinco palavras.
	 *
	 * Obrigatório porque a paleta o DESENHA à direita do rótulo e o inclui na
	 * busca: é assim que quem digita "whatsapp" chega em Canais sem saber que a
	 * tela se chama Canais — e por isso ele é traduzido junto com o rótulo, senão
	 * a busca acharia num idioma e não no outro.
	 */
	readonly hintKey: string;
	/**
	 * Os nomes que a pessoa VÊ dentro da tela e procura no ⌘K ("etiquetas" mora em
	 * Conversas e em Contatos). Só a busca lê; a linha não desenha.
	 */
	readonly termsKey?: string;
	/** Abaixo deste papel o item não é desenhado — o router já redireciona. */
	readonly minRole?: "admin";
	/** Sobre a INSTALAÇÃO, não sobre este workspace. */
	readonly platformAdmin?: true;
	/**
	 * Só no workspace que PAGA. Num workspace da rede, plano, faturas e créditos
	 * são da matriz, e oferecer a Cobrança ali seria abrir uma tela de outra casa.
	 */
	readonly payerOnly?: true;
	/** Qual dos números que o painel já lê aparece como badge, quando há um. */
	readonly count?: NavCount;
	/**
	 * O endereço tem FILHOS que são a mesma tela (`/agents/luana`, `/inbox/<id>`):
	 * a linha fica marcada neles também — o router só marca o endereço exato.
	 */
	readonly nested?: true;
	/**
	 * Desenhado no PÉ da coluna, fora do scroll — não no grupo dele.
	 *
	 * Um sinal de LUGAR, e não um item à parte: o destino continua declarado aqui,
	 * a paleta continua a encontrar e a prova de cobertura de rotas continua a ver.
	 * Tirá-lo da tabela para o pôr no rodapé seria a segunda lista de destinos, que
	 * é exatamente o que este arquivo existe para não haver.
	 */
	readonly footer?: true;
}

export type NavGroupKey = "trabalho" | "ia" | "construir" | "workspace" | "dev";

/**
 * Uma LINHA desenhada na lateral, venha ela de onde vier.
 *
 * A tela do produto que está aqui em cima, a entidade que o tenant criou e o
 * funil que ele nomeou chegam ao menu como a mesma coisa — é o que impede três
 * componentes quase iguais para três origens de dado.
 */
export interface NavRow {
	readonly to: string;
	readonly label: string;
	/** O traço. As entidades do tenant não têm um: quem as nomeou foi ele. */
	readonly icon?: IconName;
	/** Duas letras, para o rail estreito de quem não tem ícone. */
	readonly initials?: string;
	/** Zero e ausente são a mesma coisa: badge só existe quando há o que dizer. */
	readonly badge?: number;
	/**
	 * A COR do número, que diz o que ele é antes de ele ser lido.
	 *
	 * Todos os quatro badges eram violeta, e violeta ali só quer dizer "há um
	 * número" — de modo que cliente esperando há vinte minutos e missão rodando
	 * sozinha tinham exatamente o mesmo peso na periferia do olho. A V2 já
	 * separava por cor (azul, rosa, âmbar), e é a única parte do menu dela que
	 * este aqui não tinha. Ausente é o violeta de sempre.
	 */
	readonly tone?: NavTone;
	/**
	 * Quando o destino se distingue pela QUERY (`/board?funil=`), que o `RouterLink`
	 * não compara. Ausente deixa o router decidir, como em todo o resto.
	 */
	readonly active?: boolean;
}

export type NavCount = "unanswered" | "help" | "missions" | "gaps";

/** Os tons que a lateral usa — os mesmos nomes do `Badge`, sem inventar cor. */
export type NavTone = "accent" | "warning" | "danger" | "info";

/**
 * O que cada número SIGNIFICA, dito em cor.
 *
 * Não é decoração: é a ordem em que se olha. Gente esperando resposta é âmbar,
 * como a espera já é na lista de conversas; um agente travado à espera de uma
 * pessoa é vermelho, porque nada anda até alguém responder; missão rodando é
 * azul, que informa sem chamar; lacuna de memória fica no violeta do painel,
 * porque ela pode esperar até amanhã.
 */
export const COUNT_TONE: Record<NavCount, NavTone> = {
	unanswered: "warning",
	help: "danger",
	missions: "accent",
	gaps: "info",
};

/**
 * Os grupos, na ordem em que a lateral os desenha.
 *
 * `defaultOpen` é a diferença entre um menu que cabe e um que não cabe: com
 * Construir e Workspace abertos são dezenove links de ~36px empilhados, ~684px,
 * e o primeiro grupo de entidade do tenant começava por volta dos 854px — abaixo
 * da dobra numa tela de 900px. Fechados, sobram os atalhos e os cabeçalhos.
 * Quem abrir um deles fica com ele aberto (é preferência, e está no localStorage).
 *
 * ── POR QUE SÃO CINCO, E NÃO QUATRO ───────────────────────────────────────
 *
 * Porque Construir chegou a ter DOZE dos vinte e um itens do tenant, contra seis
 * de Trabalho e três de Workspace. Um grupo com mais da metade de tudo não
 * agrupa: ele é o que sobrou depois do primeiro. E a causa era o eixo, não o
 * tamanho — Trabalho × Construir divide por VERBO (o que faço hoje × o que
 * ajusto uma vez) e Workspace × Dev divide por ESCOPO (esta casa × a
 * instalação), de modo que tudo que era administrativo e não era Configurações
 * só tinha um lugar para cair. A V2 dividia por ASSUNTO (Relacionamento,
 * Operação, Catálogo, IA, Financeiro) e não tinha esse balde. IA é o assunto que
 * faltava: quem atende, o que ele oferece, o que ele faz sozinho, o que ele sabe.
 */
export const NAV_GROUPS: readonly {
	readonly key: NavGroupKey;
	readonly labelKey: string;
	readonly defaultOpen: boolean;
}[] = [
	{ key: "trabalho", labelKey: "groups.trabalho", defaultOpen: true },
	{ key: "ia", labelKey: "groups.ia", defaultOpen: false },
	{ key: "construir", labelKey: "groups.construir", defaultOpen: false },
	{ key: "workspace", labelKey: "groups.workspace", defaultOpen: false },
	{ key: "dev", labelKey: "groups.dev", defaultOpen: false },
];

/**
 * OS TRAÇOS SÃO OS DA V2 ONDE A V2 TEM UM.
 *
 * Cinco itens tinham aqui um ícone escolhido pela ideia e não pela coisa, e a
 * conta era sempre a mesma: um desenho que já significava outra coisa noutra tela.
 * O brilho de "a máquina fez sozinha" marcava Agentes (a V2 põe um robô), o raio
 * dos créditos marcava Automações (a V2 põe caixas ligadas), o ponteiro de consumo
 * marcava Painéis (a V2 põe barras), os nós de canal marcavam Memória (a V2 põe um
 * cérebro) e o envelope do canal de e-mail marcava Conversas (a V2 põe a bandeja).
 * O menu inteiro fica mais legível quando cada traço só responde por uma coisa.
 */
export const NAV_ITEMS: readonly NavItem[] = [
	// ── Trabalho: o que tem alguém do outro lado esperando ───────────────────
	{
		to: "/",
		labelKey: "items.home.label",
		icon: "home",
		group: "trabalho",
		hintKey: "items.home.hint",
	},
	{
		to: "/inbox",
		nested: true,
		labelKey: "items.inbox.label",
		icon: "inbox",
		group: "trabalho",
		hintKey: "items.inbox.hint",
		termsKey: "items.inbox.terms",
		count: "unanswered",
	},
	// Ao lado de Conversas, e não em Construir: um canal se LIGA uma vez, mas é
	// olhado no dia em que ele CAI — e quem percebe primeiro que as mensagens
	// pararam de chegar é quem atende, não quem constrói. É a mesma razão de não
	// ter `minRole` (a tela abre em leitura e esconde por dentro cada ação que o
	// servidor marca `@MinRole('admin')`), e essa razão estava a discordar do
	// lugar: enquanto ela morava em Construir, que nasce fechado, a resposta para
	// "o WhatsApp parou" ficava a dois cliques dentro de um grupo que ninguém abre
	// no meio de um atendimento. A V2 põe Canais em Relacionamento, ao lado de
	// Contatos e Funis, e é onde ele volta a ficar.
	{
		to: "/channels",
		labelKey: "items.channels.label",
		icon: "broadcast",
		group: "trabalho",
		hintKey: "items.channels.hint",
	},
	// Não confundir com "Manual": esta é a fila de perguntas que os AGENTES
	// fizeram a uma pessoa, e do outro lado dela há uma conversa parada.
	{
		to: "/help",
		labelKey: "items.help.label",
		icon: "question",
		group: "trabalho",
		hintKey: "items.help.hint",
		count: "help",
	},
	{
		to: "/contacts",
		nested: true,
		labelKey: "items.contacts.label",
		icon: "addressBook",
		group: "trabalho",
		hintKey: "items.contacts.hint",
		termsKey: "items.contacts.terms",
	},
	// "Funis" e não mais "Meus funis", em Trabalho e não mais em Construir — e as
	// duas coisas são a mesma decisão.
	//
	// Havia DUAS telas de funil no menu, em dois grupos: o kanban (`/board`) em
	// Trabalho e a lista (`/funnels`) em Construir. A divisão dizia que abrir um
	// funil é trabalho e criá-lo é construção, o que é verdade sobre o verbo e
	// falso sobre a pessoa: quem arrasta um card às dez arquiva uma etapa às
	// onze. Restou UMA tela de funis, e ela faz tudo — abre, cria, edita,
	// arquiva, define o padrão —, com o kanban como o lugar para onde ela leva.
	// Foi assim que o nome pôde encolher: "Meus funis" só existia para não
	// colidir com o `/board` que se chamava "Funis", e ele não se chama mais nada.
	//
	// Sem `minRole`, como `/channels` e pelo mesmo motivo: esta é a ÚNICA porta
	// para o funil desde que o kanban deixou de ter linha própria, e o quadro é do
	// atendente. A rota também perdeu o dela, e a tranca foi para onde ela sempre
	// deveria estar — nas seis rotas de configuração do `BoardController`, que não
	// tinha nenhuma. A tela já se desenhava por ação (`canManage`).
	{
		to: "/funnels",
		labelKey: "items.funnels.label",
		icon: "funnel",
		group: "trabalho",
		hintKey: "items.funnels.hint",
	},
	// Em Trabalho, e não mais em Construir (QA 19/10): quem LÊ o relatório é o
	// gestor no dia a dia, e um grupo que nasce fechado escondia a tela dele.
	{
		to: "/r",
		labelKey: "items.reports.label",
		icon: "chart",
		group: "trabalho",
		hintKey: "items.reports.hint",
	},

	// ── IA: quem atende, o que oferece, o que faz sozinho, o que sabe ────────
	//
	// Os três vieram de Construir, e vieram JUNTOS de propósito: subir só Agentes
	// deixaria "o que meu agente sabe" com resposta em dois grupos diferentes —
	// a persona num, a memória noutro. A V2 mantinha os três no mesmo lugar
	// (Agentes, Automações, Conhecimento) e é a parte do menu dela que funcionava.
	//
	// Automações ficou em Construir e não aqui, que é onde a V2 a punha: na V3 ela
	// não é peça de IA — o editor de regras é `src/rules/`, o MESMO que serve os
	// funis, e uma regra dispara sem modelo nenhum no caminho.
	{
		to: "/agents",
		nested: true,
		labelKey: "items.agents.label",
		icon: "bot",
		group: "ia",
		hintKey: "items.agents.hint",
	},
	// Logo abaixo de Agentes: quem acabou de ajustar o assistente é quem quer ver o
	// que ele oferece antes de alguém escrever a primeira palavra.
	{
		to: "/suggestions",
		labelKey: "items.suggestions.label",
		icon: "sparkle",
		group: "ia",
		hintKey: "items.suggestions.hint",
		minRole: "admin",
	},
	// Não é Construir, ainda que uma missão se pareça com coisa que se monta: quem
	// MONTA uma missão recorrente é a Automação (o efeito `run_mission`), e esta
	// tela é o log — o que rodou, quando e quanto custou — mais um "rodar agora".
	// E não é Trabalho porque o critério de lá é "tem alguém do outro lado
	// esperando", e numa missão não há ninguém: é o agente trabalhando sozinho, que
	// é literalmente o que o hint dela diz. Pedidos de ajuda fica em Trabalho pelo
	// mesmo critério, ao contrário: ali a conversa para até uma pessoa responder.
	//
	// O badge não se perde no grupo fechado — `NavGroup` soma os badges dos filhos
	// e desenha o total no cabeçalho de quem está recolhido.
	{
		to: "/missions",
		labelKey: "items.missions.label",
		icon: "target",
		group: "ia",
		hintKey: "items.missions.hint",
		count: "missions",
	},
	{
		to: "/memory",
		labelKey: "items.memory.label",
		icon: "brain",
		group: "ia",
		hintKey: "items.memory.hint",
		count: "gaps",
	},

	// ── Construir: o que se mexe uma vez e passa a valer sozinho ─────────────
	//
	// Site fica AQUI, e não em Workspace: é a tela mais típica deste grupo que
	// existe no menu — compõe-se uma página, publicam-se versões, cada uma com
	// endereço próprio. Pô-la entre Configurações, Manual e Cobrança diria que o
	// site é um ajuste da casa, e ele é um produto que se autora.
	{
		to: "/site",
		labelKey: "items.site.label",
		icon: "globe",
		group: "construir",
		hintKey: "items.site.hint",
		// Sem `minRole`: mexer no site segue o módulo `site` da pessoa (decisão 03/11).
	},
	// A Estrutura saiu do menu em 18/10/2026: virou a árvore de "Seu sistema",
	// logo abaixo — uma tela para ver, mudar e voltar atrás (`/structure` redireciona).
	// "Seu sistema" no lugar de Blueprints E de Artefatos (WP-C2): a vitrine é
	// uma aba dela, e a lista de funções mudou-se para o Avançado, que é seção
	// desta linha (`meta.under`). Duas linhas para o mesmo assunto eram o que
	// fazia a pessoa perguntar em qual delas se muda o quê.
	{
		to: "/system",
		labelKey: "items.system.label",
		icon: "layers",
		group: "construir",
		hintKey: "items.system.hint",
		minRole: "admin",
	},
	{
		to: "/automation",
		labelKey: "items.automation.label",
		icon: "workflow",
		group: "construir",
		hintKey: "items.automation.hint",
		minRole: "admin",
	},

	// ── Workspace: sobre a casa, não sobre o trabalho de hoje ────────────────
	//
	// Eram CINCO linhas aqui — Equipe, Conta, Consumo, Manual e Status — e o dono
	// desta instalação dizia, com razão, que não sabia onde configurava o quê.
	// Quatro delas viraram abas de `/settings`; os endereços continuam valendo e
	// `routes.ts` os leva à aba certa. Sem `minRole`: cinco das seis abas são de
	// qualquer sessão, e só a de Equipe se esconde — a tabela de abas cuida disso.
	{
		to: "/settings",
		labelKey: "items.settings.label",
		icon: "settings",
		group: "workspace",
		hintKey: "items.settings.hint",
		// No PÉ da coluna, como na V2: dentro de "Workspace" ela descia junto com o
		// grupo e sumia quando alguém o fechava. É o lugar para onde se vai quando o
		// painel já não faz o que se queria — não pode depender de onde a lista parou.
		footer: true,
	},
	// Saiu de Construir porque ali ele mentia: em Arquivos não se constrói nada.
	// A tela guarda o caderno em que os agentes anotaram e os anexos que chegaram
	// por um canal — acervo da casa, que é o que este grupo diz.
	{
		to: "/files",
		labelKey: "items.files.label",
		icon: "document",
		group: "workspace",
		hintKey: "items.files.hint",
	},
	{
		to: "/manual",
		labelKey: "items.manual.label",
		icon: "book",
		group: "workspace",
		hintKey: "items.manual.hint",
	},
	// UMA linha para cinco telas, e é de propósito: plano, créditos, limites,
	// faturas e consumo são seções de `/cobranca` (a barra dentro da tela anda
	// entre elas), e cinco linhas iguais aqui desfariam o encolher que a lateral
	// ganhou. A PALETA vê as cinco pelo nome — é a mesma divisão de trabalho que
	// as abas de Configurações já têm, e pelo mesmo motivo.
	//
	// `minRole: "admin"` porque quem paga é quem administra: o router recusa a
	// rota para um atendente, e esconder o link é só não oferecer a porta.
	{
		to: "/billing",
		labelKey: "items.billing.label",
		icon: "creditCard",
		group: "workspace",
		hintKey: "items.billing.hint",
		minRole: "admin",
		payerOnly: true,
	},

	// ── Dev: sobre a INSTALAÇÃO ──────────────────────────────────────────────
	// Um tenant que visse "Instruções do sistema" no próprio menu esperaria, com
	// razão, que fosse dele — e uma edição ali alcança todos os tenants.
	{
		to: "/platform",
		labelKey: "items.platform.label",
		icon: "server",
		group: "dev",
		hintKey: "items.platform.hint",
		platformAdmin: true,
	},
	{
		to: "/prompts",
		labelKey: "items.prompts.label",
		icon: "lines",
		group: "dev",
		hintKey: "items.prompts.hint",
		platformAdmin: true,
	},
	// Qual IA responde cada função, e em que ordem de fallback. No grupo Dev pelo
	// mesmo critério dos vizinhos: uma cadeia trocada aqui alcança todos os
	// tenants de uma vez — e vale no próximo turno, sem redeploy.
	{
		to: "/platform/models",
		labelKey: "items.models.label",
		icon: "sliders",
		group: "dev",
		hintKey: "items.models.hint",
		platformAdmin: true,
	},
	// Com que VOZ o atendente fala. Ao lado de `/platform/models` porque é a
	// mesma família de decisão — qual IA serve o quê —, mas tela própria: a voz
	// não tem cadeia de fallback, e misturá-la com as cadeias faria as duas
	// coisas parecerem a mesma.
	{
		to: "/platform/voices",
		labelKey: "items.voices.label",
		icon: "mic",
		group: "dev",
		hintKey: "items.voices.hint",
		platformAdmin: true,
	},
	// O que cada FERRAMENTA cobra em créditos (0145). Entre as vozes e o consumo
	// porque é a ponte entre eles: aqui decide-se o preço, ali vê-se o que ele deu.
	{
		to: "/platform/tool-costs",
		labelKey: "items.toolCosts.label",
		icon: "sliders",
		group: "dev",
		hintKey: "items.toolCosts.hint",
		platformAdmin: true,
	},
	// O que os modelos custaram, chamada a chamada. Ao lado de `/platform/models`
	// de propósito: são as duas metades da mesma pergunta — qual IA responde cada
	// função, e o que ela cobrou por isso.
	{
		to: "/platform/usage",
		labelKey: "items.platformUsage.label",
		icon: "chart",
		group: "dev",
		hintKey: "items.platformUsage.hint",
		platformAdmin: true,
	},
	// O dinheiro da INSTALAÇÃO, e não deste workspace. Os três estão no grupo Dev
	// pelo mesmo critério de `/platform` e `/prompts`: a edição alcança outros
	// tenants — um preço de catálogo vale para todo mundo que ainda vai escolher.
	{
		to: "/platform/billing",
		labelKey: "items.platformBilling.label",
		icon: "receipt",
		group: "dev",
		hintKey: "items.platformBilling.hint",
		platformAdmin: true,
	},
	{
		to: "/platform/catalog",
		labelKey: "items.catalog.label",
		icon: "tag",
		group: "dev",
		hintKey: "items.catalog.hint",
		platformAdmin: true,
	},
	// A VITRINE do primeiro dia: que pacotes um workspace novo recebe para
	// escolher, de que espécie são e a quem se sugerem. No grupo Dev pelo mesmo
	// critério dos vizinhos — o que se decide aqui alcança todo workspace que
	// ainda vai nascer.
	{
		to: "/platform/showcase",
		labelKey: "items.showcase.label",
		icon: "tag",
		group: "dev",
		hintKey: "items.showcase.hint",
		platformAdmin: true,
	},
	{
		to: "/platform/system-blueprints",
		labelKey: "items.systemBlueprints.label",
		icon: "tag",
		group: "dev",
		hintKey: "items.systemBlueprints.hint",
		platformAdmin: true,
	},
	{
		to: "/platform/parceiros",
		labelKey: "items.partners.label",
		icon: "handshake",
		group: "dev",
		hintKey: "items.partners.hint",
		platformAdmin: true,
	},
];

export interface NavAccess {
	readonly disabledModules?: readonly AccessModule[];
	/** `useCan("admin")`: pode administrar ESTE workspace. */
	readonly isAdmin: boolean;
	/** `usePlatformAccess()`: pode administrar a INSTALAÇÃO. Outra pergunta. */
	readonly platformAdmin: boolean;
	/**
	 * Este workspace é cobrado pela matriz (é da rede). Opcional: quem não sabe
	 * — a fiação do agente, um teste — lê como "paga a si mesmo", que é o caso
	 * de quase toda a instalação.
	 */
	readonly billedByHq?: boolean;
}

/**
 * O que esta sessão pode ver, e a mesma regra dos dois lados.
 *
 * Pura de propósito: o menu e a paleta são duas portas para a mesma tela, e um
 * filtro escrito duas vezes é um filtro que um dia vai discordar de si mesmo —
 * foi assim que `/usage` acabou visível na lateral e escondida no ⌘K.
 *
 * Esconder não é a trava: o router recusa a rota e o servidor recusa o pedido.
 * Isto só evita oferecer uma porta que não abre.
 */
export function visibleItems(items: readonly NavItem[], access: NavAccess): NavItem[] {
	return items.filter(
		(item) =>
			moduleAllowed(item.to, access.disabledModules) &&
			(!item.minRole || access.isAdmin) &&
			(!item.platformAdmin || access.platformAdmin) &&
			(!item.payerOnly || !access.billedByHq),
	);
}

/**
 * O que a PALETA oferece — as telas, mais cada aba de Configurações pelo nome.
 *
 * ── POR QUE A PALETA VÊ MAIS DO QUE O MENU ────────────────────────────────
 *
 * Porque a página única só é melhor do que cinco destinos se "senha" e "fuso
 * horário" continuarem achando o controle. Sem isto, a mudança teria trocado
 * cinco lugares óbvios da lateral por um esconderijo com seis gavetas — e o
 * defeito de que o dono reclamou ("nem sei onde configuro") teria ficado igual,
 * com uma tela nova por cima.
 *
 * O menu NÃO ganha essas linhas, e é essa a diferença entre as duas portas: a
 * lateral responde "onde eu vou", e seis abas repetidas ali desfariam o encolher
 * que é o ponto da mudança. A paleta responde "onde está a coisa que eu sei
 * nomear", e para essa pergunta a aba é a resposta certa.
 *
 * `terms` viaja separado do `hint` porque a paleta DESENHA o `hint` numa coluna
 * de fonte mono à direita do rótulo: as trinta palavras com que se procura o fuso
 * horário cabem na busca e não cabem naquela coluna.
 */
export interface PaletteDestination {
	readonly to: string;
	readonly labelKey: string;
	readonly hintKey: string;
	/** Sinônimos que a busca lê e a tela não desenha. */
	readonly termsKey?: string;
	/** Desenhado como `<prefixo> · <rótulo>` quando a linha é SEÇÃO de uma tela. */
	readonly prefixKey?: string;
	/**
	 * O texto da tabela de ORIGEM, para quando ela ainda não tem dicionário.
	 *
	 * Ninguém mais precisa dele: `features/billing/sections.ts` publicou o
	 * dicionário dela e virou tabela de ENDEREÇOS, então as cinco seções chegam
	 * aqui por chave como todo o resto. Fica declarado porque a próxima tabela de
	 * origem a nascer sem tradução tem onde cair — sem tradução, mas nunca
	 * ausente, que é a única das duas falhas que ninguém reporta.
	 */
	readonly fallback?: {
		readonly label: string;
		readonly hint: string;
		readonly terms: string;
	};
	readonly platformAdmin: boolean;
}

/**
 * AS ABAS DE "Seu sistema" QUE TÊM ENDEREÇO PRÓPRIO (24/09/2026).
 *
 * ── POR QUE ELAS SÃO DESTINOS E O MENU NÃO CRESCE ─────────────────────────
 *
 * `useTabInUrl` deu endereço a cada aba precisamente para que ela pudesse ser
 * MANDADA a alguém ("olha o histórico"). Só que ninguém as podia nomear: a
 * paleta não as tinha, e o Ben — que só navega para um destino que a lista
 * dele traz, letra por letra — dizia "liga em O que pode ter" sem conseguir
 * abrir a aba. Dizer onde fica e não saber ir é o pior dos dois mundos.
 *
 * A lateral continua com UMA linha para "Seu sistema": ela responde "onde eu
 * vou". Estas respondem "onde está a coisa que eu sei nomear", que é a
 * pergunta da paleta — e a do Ben quando alguém lhe pede para ligar um
 * complemento.
 *
 * A terceira aba (o que você TEM) fica de fora de propósito: é a que `/system`
 * abre, e um segundo endereço para a mesma tela seria uma linha repetida.
 *
 * Declaradas UMA vez e lidas por dois: a paleta abaixo e `use-destinations.ts`,
 * que é o que viaja no turno do agente.
 */
export const SYSTEM_SECTIONS: ReadonlyArray<{
	readonly to: string;
	readonly labelKey: string;
	readonly hintKey: string;
}> = [
	{
		to: "/system?aba=pode-ter",
		labelKey: "palette.system.could.label",
		hintKey: "palette.system.could.hint",
	},
	{
		to: "/system?aba=historico",
		labelKey: "palette.system.history.label",
		hintKey: "palette.system.history.hint",
	},
];

export function paletteDestinations(access: NavAccess): PaletteDestination[] {
	return [
		...visibleItems(NAV_ITEMS, access).map((item) => ({
			to: item.to,
			labelKey: item.labelKey,
			hintKey: item.hintKey,
			...(item.termsKey ? { termsKey: item.termsKey } : {}),
			platformAdmin: item.platformAdmin === true,
		})),
		/**
		 * As SEÇÕES de Cobrança, pelo nome — e a raiz fica de fora porque ela já é
		 * um destino da lateral logo acima.
		 *
		 * Mesma divisão de trabalho das abas de Configurações: a lateral responde
		 * "onde eu vou" e tem uma linha só; a paleta responde "onde está a coisa que
		 * eu sei nomear", e para essa pergunta "fatura" e "boleto" têm de chegar em
		 * `/cobranca/faturas` sem passar por Meu plano.
		 */
		...(access.isAdmin && !access.billedByHq && moduleAllowed("/billing", access.disabledModules)
			? BILLING_SECTIONS.filter((section) => section.to !== "/billing")
			: []
		).map((section) => ({
			to: section.to,
			labelKey: `palette.billing.${section.key}.label`,
			hintKey: `palette.billing.${section.key}.hint`,
			termsKey: `palette.billing.${section.key}.terms`,
			prefixKey: "palette.prefix.billing",
			platformAdmin: false,
		})),
		// As abas de "Seu sistema", como as de Configurações: prefixadas, porque na
		// lista da paleta a linha aparece entre telas e o prefixo é o que diz que
		// escolhê-la abre aquela tela naquela aba.
		...(access.isAdmin && moduleAllowed("/system", access.disabledModules)
			? SYSTEM_SECTIONS
			: []
		).map((section) => ({
			to: section.to,
			labelKey: section.labelKey,
			hintKey: section.hintKey,
			prefixKey: "palette.prefix.system",
			platformAdmin: false,
		})),
		...visibleTabs({ isAdmin: access.isAdmin, disabledModules: access.disabledModules }).map(
			(tab) => ({
				to: tabAddress(tab.value),
				// "Configurações · Sua conta" e não só "Sua conta": na lista da paleta a
				// linha aparece entre telas do produto, e o prefixo é o que diz que
				// escolhê-la abre a página de configurações naquela aba.
				labelKey: tab.labelKey,
				hintKey: tab.hintKey,
				termsKey: tab.termsKey,
				prefixKey: "palette.prefix.settings",
				platformAdmin: false,
			}),
		),
	];
}
